import { Conversation } from '@elevenlabs/client'
import { StateError } from '~/errors/StateError'
import { VoiceProvider } from '~/types/Voice'

export interface VoiceCallHooks {
  onAgentTranscript?: (transcript: string) => void
  onUserStartedSpeaking?: () => void
  onUserStoppedSpeaking?: () => void
  onAgentStartedSpeaking?: () => void
  onAgentStoppedSpeaking?: () => void
  onDisconnect?: () => void
  onError?: (error: Error) => void
  onConnect?: () => void
  onHangup?: (endedBy: string) => void
}

export function useVoiceCall(
  _provider: MaybeRefOrGetter<VoiceProvider | null>,
  hooks?: VoiceCallHooks,
) {
  // GLOBAL STATE =================================================================================
  const isSettingUp = ref(false)
  const hasError = ref(false)
  const provider = toRef(_provider)
  const callStatus = ref<
    'idle' | 'connecting' | 'connected' | 'disconnected' | 'error'
  >('idle')
  const mediaStream = ref<MediaStream | undefined>(undefined)

  const { isAboveThreshold, audioLevel: userMicrophoneLevel } =
    useAudioLevelAnalyser(mediaStream, 25)

  watch(
    isAboveThreshold,
    (newVal) => {
      if (newVal === true) {
        hooks?.onUserStartedSpeaking?.()
      } else if (newVal === false) {
        hooks?.onUserStoppedSpeaking?.()
      }
    },
    { immediate: true },
  )

  // ELEVENLABS =================================================================================
  let elevenLabsConversation: Conversation | null = null

  async function startElevenLabs(id: string) {
    try {
      elevenLabsConversation = await Conversation.startSession({
        agentId: id,
        onConnect: () => {
          hooks?.onConnect?.()
          callStatus.value = 'connected'
        },
        onDisconnect: () => {
          hooks?.onDisconnect?.()
          callStatus.value = 'disconnected'
        },
        onError: (error: string) => {
          hooks?.onError?.(new Error(error))
          console.error('Error:', error)
          callStatus.value = 'error'
        },
        onModeChange: (props: { mode: string }) => {
          if (props.mode === 'speaking') {
            hooks?.onAgentStartedSpeaking?.()
          } else {
            hooks?.onAgentStoppedSpeaking?.()
          }
        },
      })

      return elevenLabsConversation.getId()
    } catch (error) {
      hasError.value = true
      hooks?.onError?.(error as Error)
      return null
    }
  }

  function endElevenLabs() {
    if (!elevenLabsConversation) return

    elevenLabsConversation.endSession()
    elevenLabsConversation = null
  }

  // GLOBAL FUNCTIONS =================================================================================
  function $reset() {
    isSettingUp.value = false
    hasError.value = false
    elevenLabsConversation = null
    mediaStream.value = undefined
  }

  async function startCall(id: string) {
    callStatus.value = 'connecting'

    if (!provider.value) {
      captureError(
        new StateError('Attempted to start a call with no provider.'),
      )
      callStatus.value = 'error'
      return null
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    mediaStream.value = stream

    switch (provider.value) {
      case VoiceProvider.ELEVEN_LABS:
        return await startElevenLabs(id)
      default:
        captureError(
          new StateError(
            `Attempted to start a call with an invalid provider: ${provider.value}`,
          ),
        )
        return null
    }
  }

  async function endCall() {
    if (!provider.value) {
      captureError(new StateError('Attempted to end a call with no provider.'))
      callStatus.value = 'error'
      return
    }

    mediaStream.value = undefined

    switch (provider.value) {
      case VoiceProvider.ELEVEN_LABS:
        await endElevenLabs()
        return
      default:
        captureError(
          new StateError('Attempted to end a call with an invalid provider.'),
        )
    }
  }

  onUnmounted(async () => {
    await endCall()
    $reset()
  })

  return {
    startCall,
    endCall,
    $reset,
    callStatus,
    userMicrophoneLevel,
  }
}
